import { useEffect, useRef, type MouseEvent } from 'react'
import { Trash2 } from 'lucide-react'
import { Icon } from '../../platform/icons/Icon'
import { BackLink } from '../../platform/primitives/BackLink'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { RECURRING_PATH } from './recurringPaths'
import './recurring.css'

/** The list's name, as every way back to it says it. */
export const BACK_TO_LIST = 'All recurring payments'

/**
 * The back link above a bill's screens — the detail and the add/edit form.
 * `onClick` lets the form hold the navigation for its discard guard.
 */
export function RecurringBackLink({
  to = RECURRING_PATH,
  label = BACK_TO_LIST,
  onClick,
}: {
  to?: string
  label?: string
  onClick?: (e: MouseEvent) => void
}) {
  return (
    <BackLink to={to} onClick={onClick}>
      {label}
    </BackLink>
  )
}

/**
 * `is_removed`, or a 404 (shape §4.2). A fact about the bill, not a failure:
 * no alert skin, no `role="alert"`. When it arrives mid-session — the bill was
 * on screen and then vanished — `focus` moves to the heading, so a
 * screen-reader user hears why the content went.
 */
export function RemovedBill({ focus }: { focus: boolean }) {
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (focus) heading.current?.focus()
  }, [focus])
  return (
    <Panel className="rec-removed">
      <span className="rec-removed__icon">
        <Icon glyph={Trash2} size={24} />
      </span>
      <h2 ref={heading} tabIndex={-1} className="rec-removed__title">
        This bill was removed
      </h2>
      <p className="note">It was deleted on another device or on the web.</p>
      <InlineLink to={RECURRING_PATH} className="rec-removed__link">
        {BACK_TO_LIST}
      </InlineLink>
    </Panel>
  )
}
